import { IconClose } from '@holbonkit/icons'
import {
    BottomSheetBody,
    BottomSheetClose,
    BottomSheetContent,
    BottomSheetDescription,
    BottomSheetHeader,
    BottomSheetTitle,
    Box,
    Skeleton,
    Text,
    Title,
} from '@holbonkit/ui'
import dayjs from 'dayjs'

import type { SelfEmployedTransactionDetails } from '@/shared/api'

import { getSheetView, type TSheetLine } from '../../model/get-sheet-view'

import styles from './details-item.module.scss'

const DetailLine = ({ description, title, value }: TSheetLine) => {
    return (
        <Box className={styles['details-item']}>
            <Box className={styles['details-item__title']}>
                <Text variant='body-m-16-medium'>{title}</Text>
                <Text variant='body-m-16-medium'>{value}</Text>
            </Box>
            {description ?
                <Text className={styles['details-item__description']} variant='body-s-14-regular'>
                    {description}
                </Text>
            :   null}
        </Box>
    )
}

const DetailBlock = ({ lines, separated }: { lines: TSheetLine[]; separated?: boolean }) => {
    return (
        <>
            {separated ?
                <Box className={styles['bottom-sheet__body--content--separator']} />
            :   null}
            <Box className={styles['bottom-sheet__body--content']}>
                {lines.map((line) => (
                    <DetailLine key={line.title} {...line} />
                ))}
            </Box>
        </>
    )
}

export const DetailsItem = ({
    details,
    isLoading,
    isError,
}: {
    details?: SelfEmployedTransactionDetails
    isError: boolean
    isLoading: boolean
}) => {
    if (isLoading) {
        return (
            <BottomSheetContent gripper={false} showCloseButton={false}>
                <BottomSheetHeader className={styles['bottom-sheet__header']}>
                    <Box>
                        <Skeleton
                            style={{
                                height: 54,
                                width: '50%',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        />
                    </Box>
                    <BottomSheetClose>
                        <Box className={styles['bottom-sheet__header-close']}>
                            <IconClose color='var(--text-and-icon-primary)' size={20} />
                        </Box>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__body']}>
                    <Box className={styles['bottom-sheet__body--title']}>
                        <Skeleton
                            style={{
                                height: 79,
                                width: '30%',
                            }}
                        />
                        <Skeleton style={{ height: 45, width: '100%' }} />
                        <Skeleton style={{ height: 45, width: '100%' }} />
                        <Skeleton style={{ height: 45, width: '100%' }} />
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        )
    }

    if (isError) {
        return (
            <BottomSheetContent gripper={false} showCloseButton={false}>
                <BottomSheetHeader className={styles['bottom-sheet__header']}>
                    <BottomSheetTitle>Ошибка</BottomSheetTitle>
                    <BottomSheetClose>
                        <Box className={styles['bottom-sheet__header-close']}>
                            <IconClose color='var(--text-and-icon-primary)' size={20} />
                        </Box>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__body']}>
                    <Text style={{ textAlign: 'center' }} variant='body-m-16-medium'>
                        Произошла ошибка при загрузке данных
                    </Text>
                </BottomSheetBody>
            </BottomSheetContent>
        )
    }
    if (!details) {
        return null
    }

    const view = getSheetView(details)

    return (
        <BottomSheetContent gripper={false} showCloseButton={false}>
            <BottomSheetHeader className={styles['bottom-sheet__header']}>
                <BottomSheetTitle>{view.title}</BottomSheetTitle>
                {view.subtitle ?
                    <BottomSheetDescription>{view.subtitle}</BottomSheetDescription>
                :   null}
                <BottomSheetClose>
                    <Box className={styles['bottom-sheet__header-close']}>
                        <IconClose color='var(--text-and-icon-primary)' size={20} />
                    </Box>
                </BottomSheetClose>
            </BottomSheetHeader>
            <BottomSheetBody className={styles['bottom-sheet__body']}>
                <Box className={styles['bottom-sheet__body--title']}>
                    {view.amount ?
                        <Title variant='header-32-bold'>{view.amount}</Title>
                    :   null}
                    <Text
                        className={styles['bottom-sheet__body--title--description']}
                        variant='body-s-14-regular'
                    >
                        {dayjs(details.date).format('DD.MM.YYYY в HH:mm')}
                    </Text>
                </Box>
                {view.main ?
                    <DetailBlock lines={[view.main]} />
                :   null}
                {view.fees ?
                    <DetailBlock separated lines={view.fees} />
                :   null}
                {view.total ?
                    <DetailBlock separated lines={[view.total]} />
                :   null}
            </BottomSheetBody>
        </BottomSheetContent>
    )
}
